import {
  MANUAL_SLOT_COUNT,
  listSlots,
  saveToSlot,
  loadFromSlot,
  deleteSlot,
  type SaveEntry,
} from '../engine/save-manager';
import type { GameState } from '../engine/game';

export type SaveLoadMode = 'save' | 'load';

export interface SaveLoadModalCallbacks {
  /** Called when a slot is loaded. */
  onLoad: (state: GameState) => void;
  /** Called when the modal is closed without action. */
  onClose: () => void;
  /** Returns the current game state for saving (only used in 'save' mode). */
  getCurrentState?: () => { state: GameState; scriptTitle: string };
}

/**
 * Renders a save/load modal overlay.
 * In 'save' mode, clicking a slot saves the current state.
 * In 'load' mode, clicking a slot loads the saved state.
 */
export function renderSaveLoadModal(
  root: HTMLElement,
  mode: SaveLoadMode,
  callbacks: SaveLoadModalCallbacks,
): void {
  // Remove any existing modal
  const existing = root.querySelector('#save-load-modal');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'save-load-modal';

  const panel = document.createElement('div');
  panel.className = 'sl-panel';

  // Header
  const header = document.createElement('div');
  header.className = 'sl-header';

  const title = document.createElement('h2');
  title.textContent = mode === 'save' ? 'Save Game' : 'Load Game';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'sl-close';
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('click', () => {
    close();
  });
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Slots
  const slotsContainer = document.createElement('div');
  slotsContainer.className = 'sl-slots';

  function renderSlots(): void {
    slotsContainer.innerHTML = '';
    const slots = listSlots();

    for (let i = 0; i < MANUAL_SLOT_COUNT; i++) {
      const slotNum = i + 1;
      const entry = slots[i];
      const slotEl = createSlotElement(slotNum, entry, mode, callbacks, () => {
        overlay.remove();
      }, renderSlots);
      slotsContainer.appendChild(slotEl);
    }
  }

  renderSlots();
  panel.appendChild(slotsContainer);
  overlay.appendChild(panel);

  // Close on overlay background click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      close();
    }
  });

  // Esc to close
  const escHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', escHandler);

  root.appendChild(overlay);

  function close(): void {
    document.removeEventListener('keydown', escHandler);
    overlay.remove();
    callbacks.onClose();
  }
}

function createSlotElement(
  slotNum: number,
  entry: SaveEntry | null,
  mode: SaveLoadMode,
  callbacks: SaveLoadModalCallbacks,
  closeModal: () => void,
  refreshSlots: () => void,
): HTMLDivElement {
  const slotEl = document.createElement('div');
  slotEl.className = 'sl-slot';

  const info = document.createElement('div');
  info.className = 'sl-slot-info';

  const label = document.createElement('div');
  label.className = 'sl-slot-label';
  label.textContent = `Slot ${slotNum}`;
  info.appendChild(label);

  if (entry) {
    const detail = document.createElement('div');
    detail.className = 'sl-slot-detail';
    const date = new Date(entry.meta.timestamp);
    detail.textContent = `${entry.meta.scriptTitle} — ${formatDate(date)}`;
    info.appendChild(detail);
  } else {
    const detail = document.createElement('div');
    detail.className = 'sl-slot-detail sl-slot-empty';
    detail.textContent = '— Empty —';
    info.appendChild(detail);
  }

  slotEl.appendChild(info);

  const actions = document.createElement('div');
  actions.className = 'sl-slot-actions';

  if (mode === 'save') {
    const saveBtn = document.createElement('button');
    saveBtn.className = 'sl-btn sl-btn-save';
    saveBtn.textContent = entry ? 'Overwrite' : 'Save';
    saveBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (callbacks.getCurrentState) {
        const { state, scriptTitle } = callbacks.getCurrentState();
        saveToSlot(slotNum, state, scriptTitle);
        refreshSlots();
      }
    });
    actions.appendChild(saveBtn);
  } else {
    // Load mode
    const loadBtn = document.createElement('button');
    loadBtn.className = 'sl-btn sl-btn-load';
    loadBtn.textContent = 'Load';
    loadBtn.disabled = !entry;
    loadBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const loaded = loadFromSlot(slotNum);
      if (loaded) {
        closeModal();
        callbacks.onLoad(loaded.state);
      }
    });
    actions.appendChild(loadBtn);
  }

  // Delete button (only if slot has data)
  if (entry) {
    const delBtn = document.createElement('button');
    delBtn.className = 'sl-btn sl-btn-delete';
    delBtn.textContent = 'Delete';
    delBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      deleteSlot(slotNum);
      refreshSlots();
    });
    actions.appendChild(delBtn);
  }

  slotEl.appendChild(actions);
  return slotEl;
}

function formatDate(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
